// Copyright (c) 2026 Norkron. All Rights Reserved.
// Proprietary and confidential. See LICENSE file.

/**
 * Avatar resolver — the single source of truth for the avatar SHAPE (by actor
 * type) and the deterministic fallback COLOR (by identity). One resolver, one
 * `<Avatar>` component: every named surface (notes, replies, history, activity)
 * consumes this, so avatar behavior is never re-derived per-surface.
 *
 * The visual distinction is the core requirement:
 *   · user      → CIRCLE avatar
 *   · agent     → SQUARE-with-soft-corners avatar (an AI reads differently at a glance)
 *   · workspace → rounded-square BADGE
 *   · system    → BADGE (the ◆ glyph)
 */

import type { ActorRef, ActorType } from "../api/types";
import { ACCENTS, accentOn } from "./accents";
import { initials } from "./format";

export type AvatarShape = "circle" | "square-soft" | "badge";

/** The shape a given actor type renders as. Shape is the load-bearing visual
 *  rule: agents MUST be square-soft, humans MUST be circles. */
export function resolveAvatarShape(type: ActorType): AvatarShape {
  switch (type) {
    case "agent":
      return "square-soft";
    case "workspace":
    case "system":
      return "badge";
    default:
      return "circle";
  }
}

/** The ordered accent-hue keys (stable insertion order) the fallback color hash
 *  indexes into. Reusing `ACCENTS` keeps generated avatars ON-BRAND, and the
 *  on-accent initials colour comes from `accentOn`, which MEASURES contrast per
 *  hue AND per theme rather than reading a hand-typed field.
 *
 *  ⚠️ It used to read `ACCENTS[hue].on`, a single theme-independent value. That
 *  field is gone because it could not be right for both themes and was measurably
 *  wrong for three hues — verdigris/terracotta/slate-teal in dark theme painted
 *  white initials at 2.54 / 2.62 / 2.76:1, below WCAG's 3:1 floor. Avatar
 *  initials render at xs, so those were the worst-affected surface in the app. */
const HUES = Object.keys(ACCENTS);

/** FNV-1a (32-bit) over a stable identity string. Deterministic: the SAME actor
 *  always resolves to the SAME hue across every surface and every reload. */
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts (avoids float precision loss).
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/** The stable identity key an actor's fallback color hashes over. Agents (and
 *  any actor without a DB id) key off the NAME so distinct agents get distinct
 *  colors even though they share id 0; identified actors key off `type:id`. */
export function avatarColorKey(type: ActorType, id: number, name: string): string {
  return id > 0 ? `${type}:${id}` : `${type}:${name}`;
}

/** The resolved fallback color for an actor: a stable pick from the 8-hue accent
 *  palette. Returns the hue KEY plus its resolved `bg`/`on` pair for the current
 *  theme (both stops are the same hue in light + dark). */
export function resolveAvatarColor(
  type: ActorType,
  id: number,
  name: string,
  theme: "light" | "dark" = "light",
): { hue: string; bg: string; on: string } {
  const hue = HUES[fnv1a(avatarColorKey(type, id, name)) % HUES.length];
  const accent = ACCENTS[hue];
  return {
    hue,
    bg: theme === "dark" ? accent.dark : accent.light,
    on: accentOn(hue, theme),
  };
}

/** The CSS custom properties that paint an actor's deterministic fallback fill.
 *  Emits BOTH theme stops (`--ela-avatar-bg` light, `--ela-avatar-bg-dark` dark)
 *  so `avatar.css` picks the right one per theme with no re-render.
 *
 *  ⚠️ The INITIALS colour is dual-stop for the same reason the fill is. It used
 *  to be a single `--ela-avatar-on`, which forced one hand-typed colour to serve
 *  both themes; the derived value differs per theme for five of the eight hues
 *  (a pale dark-theme stop wants DARK text), so a single value is wrong by
 *  construction. `avatar.css` selects `--ela-avatar-on-dark` under
 *  `[data-theme="dark"]` exactly as it already did for the background. */
export function avatarFillVars(
  type: ActorType,
  id: number,
  name: string,
): Record<string, string> {
  const hue = HUES[fnv1a(avatarColorKey(type, id, name)) % HUES.length];
  const accent = ACCENTS[hue];
  return {
    "--ela-avatar-bg": accent.light,
    "--ela-avatar-bg-dark": accent.dark,
    "--ela-avatar-on": accentOn(hue, "light"),
    "--ela-avatar-on-dark": accentOn(hue, "dark"),
  };
}

/** Wrap a legacy `*_name` string into a minimal ActorRef so a mount can degrade
 *  gracefully when the server hasn't populated the full `actor` field yet. */
export function actorFromLegacy(name: string | null | undefined, type: ActorType = "user"): ActorRef {
  return { id: 0, type, name: name ?? "", avatar_url: null };
}

/** The fully-resolved view a `<Avatar>` renders from an actor ref: name, type,
 *  shape, the image url (or null → fallback), the fallback initials, and the
 *  deterministic hue key. Pure + deterministic. */
export function resolveActor(ref: ActorRef): {
  name: string;
  type: ActorType;
  shape: AvatarShape;
  avatarUrl: string | null;
  initials: string;
  hue: string;
} {
  return {
    name: ref.name,
    type: ref.type,
    shape: resolveAvatarShape(ref.type),
    avatarUrl: ref.avatar_url ?? null,
    initials: ref.name ? initials(ref.name) : "?",
    hue: resolveAvatarColor(ref.type, ref.id, ref.name).hue,
  };
}
